import React, { useEffect, useRef } from "react";
import { getLandPoints, getGraticule } from "./landmask";

/**
 * Rotating orthographic dot-globe — pure canvas, no libraries.
 *
 * This is a faithful port of the draw() loop in the canonical mockup
 * (ttec_auth_vivid_unified.html): real continents from the 1° land mask
 * (landmask.ts), -20° axial tilt, graticule dots, depth-shaded land dots,
 * a radial sphere body and a rim stroke.
 *
 * `variant="bright"` matches the Vivid brand panel (#globeMain): navy land +
 * white accents on the light gradient. `variant="dark"` matches the SSO
 * "Signing you in" screen (#globeSSO): cyan land + neon-green accents on dark.
 */
export type GlobeVariant = "bright" | "dark";

export interface DotGlobeProps {
  className?: string;
  /** CSS pixel size of the (square) canvas. Mockup: 520 (web), 120 (SSO). */
  size?: number;
  /** Palette + sphere shading preset. */
  variant?: GlobeVariant;
  /** Radians added to the spin each frame (default 0.005; mockup used 0.0015). */
  spin?: number;
}

interface Palette {
  land: string; // "r,g,b"
  accent: string; // "r,g,b"
  grat: string; // "r,g,b,a"
  rim: string; // "r,g,b,a"
}

const PALETTES: Record<GlobeVariant, Palette> = {
  bright: {
    land: "1,34,71",
    accent: "255,255,255",
    grat: "255,255,255,0.16",
    rim: "255,255,255,0.42",
  },
  dark: {
    land: "101,255,255",
    accent: "2,245,106",
    grat: "101,255,255,0.16",
    rim: "101,255,255,0.28",
  },
};

const DEG = Math.PI / 180;
const TILT = -20 * DEG;
const sT = Math.sin(TILT);
const cT = Math.cos(TILT);
// Backing store at 2× — the mockup authors #globeMain at 1040 for a 520 css box.
const SCALE = 2;

const DotGlobe: React.FC<DotGlobeProps> = ({
  className,
  size = 520,
  variant = "bright",
  spin = 0.005,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const W = Math.round(size * SCALE);
    canvas.width = W;
    canvas.height = W;

    const land = getLandPoints();
    const grat = getGraticule();
    const pal = PALETTES[variant];
    const bright = variant === "bright";

    // All geometry derives from the backing width W (mockup convention).
    const R = W * 0.46;
    const cx = W / 2;
    const cy = W / 2;
    const k = W / 400;

    const reduceMotion =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const draw = (rot: number): void => {
      ctx.clearRect(0, 0, W, W);

      // sphere body
      const g = ctx.createRadialGradient(
        cx - R * 0.32,
        cy - R * 0.36,
        R * 0.1,
        cx,
        cy,
        R
      );
      if (bright) {
        g.addColorStop(0, "rgba(255,255,255,0.10)");
        g.addColorStop(0.55, "rgba(1,34,71,0.10)");
        g.addColorStop(1, "rgba(1,20,45,0.32)");
      } else {
        g.addColorStop(0, "rgba(60,173,255,0.34)");
        g.addColorStop(0.6, "rgba(4,149,255,0.12)");
        g.addColorStop(1, "rgba(1,42,87,0.02)");
      }
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.arc(cx, cy, R, 0, Math.PI * 2);
      ctx.fill();

      // graticule dots
      ctx.fillStyle = "rgba(" + pal.grat + ")";
      for (let i = 0; i < grat.length; i++) {
        const lo = grat[i].lon;
        const la = grat[i].lat;
        const cp = Math.cos(la);
        const cc = sT * Math.sin(la) + cT * cp * Math.cos(lo - rot);
        if (cc < 0) continue;
        const x = cp * Math.sin(lo - rot);
        const y = cT * Math.sin(la) - sT * cp * Math.cos(lo - rot);
        ctx.beginPath();
        ctx.arc(cx + x * R, cy - y * R, 0.6 * k, 0, Math.PI * 2);
        ctx.fill();
      }

      // land dots
      for (let i = 0; i < land.length; i++) {
        const lo = land[i].lon;
        const la = land[i].lat;
        const cp = Math.cos(la);
        const cc = sT * Math.sin(la) + cT * cp * Math.cos(lo - rot);
        if (cc < 0) continue;
        const x = cp * Math.sin(lo - rot);
        const y = cT * Math.sin(la) - sT * cp * Math.cos(lo - rot);
        const a = 0.25 + cc * 0.6;
        const r = (0.62 + cc * 1.25) * k;
        ctx.fillStyle = land[i].accent
          ? "rgba(" + pal.accent + "," + a + ")"
          : "rgba(" + pal.land + "," + a + ")";
        ctx.beginPath();
        ctx.arc(cx + x * R, cy - y * R, r, 0, Math.PI * 2);
        ctx.fill();
      }

      // rim
      ctx.strokeStyle = "rgba(" + pal.rim + ")";
      ctx.lineWidth = k;
      ctx.beginPath();
      ctx.arc(cx, cy, R, 0, Math.PI * 2);
      ctx.stroke();
    };

    let rot = 0;
    let alive = true;

    const loop = (): void => {
      if (!alive) return;
      rot -= spin; // west->east (eastward) — matches a real globe, north up
      draw(rot);
      rafRef.current = requestAnimationFrame(loop);
    };

    if (reduceMotion) {
      draw(0.6); // single static frame
    } else {
      rafRef.current = requestAnimationFrame(loop);
    }

    return () => {
      alive = false;
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, [size, variant, spin]);

  return (
    <canvas
      ref={canvasRef}
      className={className}
      style={{ width: size, height: size }}
      aria-hidden="true"
    />
  );
};

export default DotGlobe;
